import {
  motion,
} from "framer-motion";

import {
  Link,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import {
  getRoutePath,
} from "../../../site/config/routes";

import aboutData from "../../../site/data/about";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  ABOUT / PHOTOGRAPHER
  ============================================================

  Etusivun kuvaajaosio.

  Desktop:
  - teksti vasemmalla
  - suuri henkilökuva oikealla
  - epäsymmetrinen editorial-taitto

  Mobile:
  - teksti ja kuva pinoutuvat luonnollisesti
*/

export default function AboutSection({
  language = "fi",
}) {
  const {
    t,
  } = useTranslation("common");

  return (
    <section
      id="about"
      className="
        relative
        overflow-hidden
        border-t
        border-border
        bg-canvas
      "
    >
      <div
        className="
          mx-auto
          w-full
          max-w-editorial
          px-6
          py-24

          sm:px-10
          sm:py-32

          lg:px-16
          lg:py-40
        "
      >
        {/* ====================================================
            TOP META
            ==================================================== */}

        <motion.div
          className="
            mb-14
            flex
            items-center
            justify-between
            gap-8
            border-b
            border-border
            pb-5

            sm:mb-20
          "
          initial={{
            opacity: 0,
            y: 16,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.5,
          }}
          transition={{
            duration: 0.75,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <p
            className="
              m-0
              text-[9px]
              font-medium
              uppercase
              tracking-[0.28em]
              text-muted

              sm:text-[10px]
            "
          >
            04 /{" "}
            {t(
              "aboutSection.eyebrow"
            )}
          </p>

          <span
            className="
              h-px
              w-12
              bg-border

              sm:w-20
            "
            aria-hidden="true"
          />
        </motion.div>

        {/* ====================================================
            EDITORIAL LAYOUT
            ==================================================== */}

        <div
          className="
            grid
            gap-14

            lg:grid-cols-[minmax(0,0.72fr)_minmax(420px,1fr)]
            lg:items-center
            lg:gap-20
          "
        >
          {/* ==================================================
              TEXT
              ================================================== */}

          <motion.div
            className="
              relative
              z-10

              lg:pr-4
            "
            initial={{
              opacity: 0,
              x: -28,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
              amount: 0.3,
            }}
            transition={{
              duration: 0.95,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <p
              className="
                mb-6
                text-[9px]
                font-medium
                uppercase
                tracking-[0.25em]
                text-muted

                sm:text-[10px]
              "
            >
              {t(
                "aboutSection.intro"
              )}
            </p>

            <h2
              className="
                m-0
                max-w-3xl
                font-display
                text-[clamp(3.2rem,6.5vw,7rem)]
                font-normal
                leading-[0.88]
                tracking-[-0.05em]
                text-text
              "
            >
              {t(
                "aboutSection.title"
              )}
            </h2>

            <div
              className="
                my-9
                h-px
                w-12
                bg-text

                sm:my-11
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                max-w-lg
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {t(
                "aboutSection.description"
              )}
            </p>

            <p
              className="
                mb-0
                mt-6
                max-w-lg
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {t(
                "aboutSection.descriptionTwo"
              )}
            </p>

            <Link
              to={getRoutePath(
                "about",
                language
              )}
              className="
                group
                mt-10
                inline-flex
                items-center
                gap-4
                border-b
                border-text
                pb-2
                text-[10px]
                font-semibold
                uppercase
                tracking-[0.2em]
                text-text
                no-underline
              "
            >
              {t(
                "aboutSection.link"
              )}

              <span
                className="
                  transition-transform
                  duration-300
                  group-hover:translate-x-1
                "
                aria-hidden="true"
              >
                →
              </span>
            </Link>
          </motion.div>

          {/* ==================================================
              IMAGE
              ================================================== */}

          <motion.div
            className="
              relative

              lg:pl-5
            "
            initial={{
              opacity: 0,
              y: 36,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.2,
            }}
            transition={{
              duration: 1,
              delay: 0.08,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <div
              className="
                relative
                ml-auto
                aspect-[4/5]
                w-full
                overflow-hidden
                bg-surface

                sm:aspect-[5/6]
                lg:max-w-[650px]
              "
            >
              <img
                src={
                  aboutData.image
                }
                alt={t(
                  "aboutSection.imageAlt"
                )}
                className="
                  h-full
                  w-full
                  object-cover
                  transition-transform
                  duration-[1200ms]
                  ease-out

                  hover:scale-[1.015]
                "
                style={{
                  objectPosition:
                    aboutData.imagePosition,
                }}
              />

              <div
                className="
                  pointer-events-none
                  absolute
                  inset-0
                  border
                  border-white/10
                "
                aria-hidden="true"
              />
            </div>

            {/* editorial caption */}

            <div
              className="
                mt-5
                flex
                items-center
                justify-between
                gap-6
              "
            >
              <p
                className="
                  m-0
                  text-[9px]
                  uppercase
                  tracking-[0.22em]
                  text-muted

                  sm:text-[10px]
                "
              >
                {t(
                  "aboutSection.caption"
                )}
              </p>

              <span
                className="
                  font-display
                  text-3xl
                  italic
                  text-muted

                  sm:text-4xl
                "
                aria-hidden="true"
              >
                04
              </span>
            </div>
          </motion.div>
        </div>

        {/* ====================================================
            QUOTE
            ==================================================== */}

        <motion.div
          className="
            mt-24
            border-t
            border-border
            pt-10

            sm:mt-32
            sm:pt-14

            lg:mt-40
          "
          initial={{
            opacity: 0,
            y: 24,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.4,
          }}
          transition={{
            duration: 0.9,
          }}
        >
          <blockquote
            className="
              m-0
              ml-auto
              max-w-4xl
              font-display
              text-[clamp(2rem,4.2vw,4.8rem)]
              font-normal
              leading-[0.98]
              tracking-[-0.035em]
              text-text
            "
          >
            “{t(
              "aboutSection.quote"
            )}”
          </blockquote>
        </motion.div>
      </div>
    </section>
  );
}
